import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { onIdTokenChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { Mail, CheckCircle2 } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';

export default function EmailVerificationPendingPage() {
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const { currentUser, userProfile, sendVerificationEmail, refreshUserProfile } = useAuth();
  const navigate = useNavigate();

  // Redirect as soon as Auth or Firestore shows verified (no User.reload on every render)
  useEffect(() => {
    if (currentUser?.emailVerified || userProfile?.emailVerified) {
      console.log('✅ Email already verified, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser?.emailVerified, userProfile?.emailVerified, navigate]);

  // When the ID token changes (e.g. after verification), sync profile without polling reload() aggressively
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, () => {
      void refreshUserProfile().catch(() => {
        /* errors logged inside refreshUserProfile; avoid unhandled rejection */
      });
    });
    return unsub;
  }, [refreshUserProfile]);

  // Light backup poll while this tab is visible (5s was overloading Identity Toolkit on slow links)
  useEffect(() => {
    void refreshUserProfile().catch(() => {});
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshUserProfile().catch(() => {});
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshUserProfile]);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      const verified = await refreshUserProfile();
      if (!verified) {
        showError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error: unknown) {
      console.error('Error checking verification status:', error);
      const code =
        error && typeof error === 'object' && 'code' in error
          ? (error as { code?: string }).code
          : undefined;
      if (code === 'auth/network-request-failed') {
        showError(
          'The connection timed out. Check your network and tap "I\'ve Verified - Check Status" again.',
        );
      } else {
        showError('Failed to check verification status. Please try again.');
      }
    } finally {
      setChecking(false);
    }
  };

  const handleResendEmail = async () => {
    if (!sendVerificationEmail) {
      showError('Email verification is not available.');
      return;
    }
    
    setResending(true);
    try {
      await sendVerificationEmail('/dashboard');
      showSuccess('Verification email resent successfully!');
    } catch (error) {
      console.error('Error resending email:', error);
      showError('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <Mail className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle>Verify Your Email Address</CardTitle>
            <CardDescription>
              We've sent a verification email to protect your information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-gray-700">
                We have sent a verification email to{' '}
                <strong className="font-semibold">{currentUser?.email}</strong>
              </p>
            </div>

            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                To complete your registration and access all features, please click the verification
                link in the email we just sent you.
              </p>
              <p>
                This verification step helps us protect sensitive Masonic information and ensures
                that only authorized members can access event details and dining arrangements.
              </p>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <p className="font-semibold mb-1">What happens after verification?</p>
                  <p>
                    Once you verify your email, we'll automatically link any existing RSVPs
                    associated with your email address to your account, so you can manage them all
                    in one place.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleCheckStatus}
                className="w-full"
                disabled={checking}
              >
                {checking ? 'Checking...' : 'I\'ve Verified - Check Status'}
              </Button>
              
              <Button
                onClick={handleResendEmail}
                variant="outline"
                className="w-full"
                disabled={resending}
              >
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                Didn't receive the email? Check your spam folder or click the button above.
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="text-center text-sm">
                <span className="text-muted-foreground">Already verified? </span>
                <Link to="/login" className="text-primary hover:underline">
                  Log in
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

